import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, RotateCcw, Camera, Anchor, Mail, Copy, Check, FileText, Ship, ListChecks, ShoppingBag, Monitor, HardDrive, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { CHECKLIST_TYPE_LABEL } from "../lib/constants";
import DocumentUploader, { DocumentRow } from "../components/DocumentUploader";
import BoatsManager from "../components/BoatsManager";
import ChecklistTypesManager from "../components/ChecklistTypesManager";
import ProductsManager from "../components/ProductsManager";
import TvScreenSettings from "../components/TvScreenSettings";

const TYPES = ["maintenance", "checkin", "checkout"];

export default function SettingsPage() {
  const [templates, setTemplates] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [tab, setTab] = useState("maintenance");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ label_no: "", label_en: "", photo_on_fail: false });

  const load = async () => {
    const [t, o] = await Promise.all([
      api.get("/checklist-templates"),
      api.get("/admin/checklist-templates/overrides"),
    ]);
    setTemplates(t.data);
    setOverrides(o.data);
  };

  useEffect(() => {
    Promise.all([
      api.get("/checklist-templates"),
      api.get("/admin/checklist-templates/overrides"),
    ]).then(([t, o]) => {
      setTemplates(t.data);
      setOverrides(o.data);
    });
  }, []);

  const isHidden = (type, key) =>
    overrides.some((o) => o.type === type && o.key === key && o.action === "hide");
  const isCustom = (type, key) =>
    overrides.some((o) => o.type === type && o.key === key && o.action === "add");

  const hide = async (type, key) => {
    await api.post("/admin/checklist-templates/hide", { type, key });
    toast.success("Skjult fra sjekklisten");
    load();
  };
  const restore = async (type, key) => {
    await api.post("/admin/checklist-templates/restore", { type, key });
    toast.success("Gjenopprettet");
    load();
  };
  const remove = async (type, key) => {
    if (!window.confirm("Slette dette egendefinerte punktet?")) return;
    await api.post("/admin/checklist-templates/hide", { type, key });
    toast.success("Slettet");
    load();
  };

  const onAdd = async () => {
    if (!form.label_no.trim()) return toast.error("Skriv inn et navn");
    await api.post("/admin/checklist-templates/items", {
      type: tab,
      label_no: form.label_no.trim(),
      label_en: form.label_en.trim() || form.label_no.trim(),
      photo_on_fail: form.photo_on_fail,
    });
    toast.success("Punkt lagt til");
    setForm({ label_no: "", label_en: "", photo_on_fail: false });
    setAddOpen(false);
    load();
  };

  // Show *all* items including hidden ones so admin can restore them
  const allItemsFor = (type) => {
    const visible = templates[type] || [];
    const visibleKeys = new Set(visible.map((v) => v.key));
    const hidden = overrides
      .filter((o) => o.type === type && o.action === "hide")
      .filter((o) => !visibleKeys.has(o.key))
      .map((o) => ({ key: o.key, label_no: `(skjult) ${o.key}`, label_en: "", isHidden: true }));
    return [...visible.map((v) => ({ ...v, isHidden: false })), ...hidden];
  };

  // ----- Global documents state -----
  const [globalDocs, setGlobalDocs] = useState([]);

  const loadGlobalDocs = async () => {
    try {
      const r = await api.get("/documents", { params: { scope: "global" } });
      setGlobalDocs(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadGlobalDocs(); }, []);

  const deleteGlobalDoc = async (id) => {
    if (!window.confirm("Slette dokumentet? Det vil ikke lenger vises hos kundene.")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Slettet");
    loadGlobalDocs();
  };

  // ----- Rentware integration state -----
  const [rwLoaded, setRwLoaded] = useState(false);
  const [rwToken, setRwToken] = useState("");
  const [rwLocale, setRwLocale] = useState("nb-NO");
  const [boats, setBoats] = useState([]);
  const [boatDraft, setBoatDraft] = useState({}); // {boat_id: {embed, articleId}}
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/rentware"),
      api.get("/boats"),
    ]).then(([s, b]) => {
      setRwToken(s.data.access_token || "");
      setRwLocale(s.data.locale || "nb-NO");
      setBoats(b.data);
      setBoatDraft(
        Object.fromEntries(
          b.data.map((bo) => [bo.id, {
            embed: bo.rentware_embed_html || "",
            articleId: bo.rentware_article_id || "",
          }]),
        ),
      );
      setRwLoaded(true);
    });
  }, []);

  const saveRentware = async () => {
    await api.put("/settings/rentware", { access_token: rwToken.trim(), locale: rwLocale });
    toast.success("Rentware lagret – last siden på nytt for at endringen skal tre i kraft.");
  };

  const saveBoat = async (boatId) => {
    const d = boatDraft[boatId] || {};
    await api.patch(`/boats/${boatId}`, {
      rentware_embed_html: d.embed || "",
      rentware_article_id: d.articleId || "",
    });
    toast.success("Båt oppdatert");
  };

  const supportEmail = `Subject: Tilgang til Rentware API / iCal-feed for Canal Boats Telemark

Hei Rentware-support,

Vi driver Canal Boats Telemark (canalboats.me) – elektriske leiebåter i Telemarkskanalen – og har bygget en intern flåteadministrasjons-app som synkroniserer bookinger med inn/utsjekk-sjekklister, vedlikeholdsplaner og kundeportal.

Vi ønsker å koble appen direkte til Rentware-kontoen vår slik at vi kan:

1. Hente liste over kommende og pågående ordrer/bookinger pr. artikkel (båt) på en strukturert måte (JSON eller iCal/.ics).
2. Få beskjed (webhook eller polling) når en ny booking opprettes eller endres.
3. Lese kundeinformasjon (navn, antall personer, kontakt) som er knyttet til bookingen.

Kan dere bekrefte:
a) Tilbys en REST API for bookinger/ordrer på vår plan? I så fall: hvordan får jeg API-nøkkel og dokumentasjon?
b) Eksisterer en iCal-/ICS-eksport pr. artikkel som vi kan polle?
c) Webhooks ved ordreopprettelse/-endring?

Vi har allerede widget-token (W…f1229607825ede9b2) og bruker widgetene på kundesidene, men trenger server-til-server-tilgang for backend-flyten.

På forhånd takk – ser frem til svar.

Med vennlig hilsen,
[Ditt navn]
Canal Boats Telemark
`;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
    } catch {
      // fallback for iframe / non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = supportEmail;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2500);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">System</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Innstillinger</h1>
        <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
          Tilpass sjekklister og koble flåten til eksterne system som Rentware.
        </p>
      </div>

      <Tabs defaultValue="checklists" className="w-full">
        <TabsList className="bg-[#0C1824]/5 border border-[#BD984D]/25 flex-wrap h-auto">
          <TabsTrigger value="checklists" data-testid="settings-section-checklists"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            Sjekklister
          </TabsTrigger>
          <TabsTrigger value="checklist-types" data-testid="settings-section-checklist-types"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <ListChecks className="w-4 h-4 mr-1.5" /> Sjekkliste-typer
          </TabsTrigger>
          <TabsTrigger value="boats" data-testid="settings-section-boats"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <Ship className="w-4 h-4 mr-1.5" /> Båter
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="settings-section-products"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <ShoppingBag className="w-4 h-4 mr-1.5" /> Produkter
          </TabsTrigger>
          <TabsTrigger value="rentware" data-testid="settings-section-rentware"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <Anchor className="w-4 h-4 mr-1.5" /> Rentware
          </TabsTrigger>
          <TabsTrigger value="tv-screen" data-testid="settings-section-tv-screen"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <Monitor className="w-4 h-4 mr-1.5" /> Kontorskjerm
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="settings-section-documents"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-1.5" /> Standarddokumenter
          </TabsTrigger>
          <TabsTrigger value="drive" data-testid="settings-section-drive"
                       className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
            <HardDrive className="w-4 h-4 mr-1.5" /> Google Drive
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checklists" className="mt-6 space-y-3">
          <p className="text-sm text-[#2B3F4D] max-w-2xl">
            Tilpass innholdet i sjekklistene. Legg til egne punkter, eller skjul forhåndsdefinerte
            punkter som ikke gjelder for din drift. «Ikke nødvendig»-status er alltid tilgjengelig
            (f.eks. for sengetøy på dagsturer).
          </p>

          <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TYPES.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`settings-tab-${t}`}>
              {CHECKLIST_TYPE_LABEL[t]}
            </TabsTrigger>
          ))}
        </TabsList>

        {TYPES.map((type) => (
          <TabsContent key={type} value={type} className="mt-5 space-y-3">
            <div className="flex justify-end">
              <Dialog open={addOpen && tab === type} onOpenChange={(o) => setAddOpen(o)}>
                <DialogTrigger asChild>
                  <Button
                    data-testid={`settings-add-${type}`}
                    className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Legg til punkt
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nytt punkt – {CHECKLIST_TYPE_LABEL[type]}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Tekst (norsk)</Label>
                      <Input
                        value={form.label_no}
                        onChange={(e) => setForm({ ...form, label_no: e.target.value })}
                        data-testid="settings-add-label-no"
                        placeholder="F.eks. Sjekk redningsvester"
                      />
                    </div>
                    <div>
                      <Label>Tekst (engelsk, valgfri)</Label>
                      <Input
                        value={form.label_en}
                        onChange={(e) => setForm({ ...form, label_en: e.target.value })}
                        data-testid="settings-add-label-en"
                        placeholder="E.g. Check life vests"
                      />
                    </div>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="photo"
                        checked={form.photo_on_fail}
                        onCheckedChange={(v) => setForm({ ...form, photo_on_fail: !!v })}
                        data-testid="settings-add-photo"
                      />
                      <Label htmlFor="photo" className="text-sm font-normal cursor-pointer">
                        <Camera className="w-3.5 h-3.5 inline mr-1" />
                        Krev bilde ved avvik på dette punktet
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddOpen(false)}>Avbryt</Button>
                    <Button
                      onClick={onAdd}
                      data-testid="settings-add-submit"
                      className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                    >
                      Legg til
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="overflow-hidden">
              <div className="divide-y divide-[#E2E8F0]">
                {allItemsFor(type).map((it) => {
                  const custom = isCustom(type, it.key);
                  const hidden = isHidden(type, it.key);
                  return (
                    <div
                      key={it.key}
                      className={`px-4 py-3 flex items-center justify-between gap-3 ${
                        hidden ? "bg-slate-50 opacity-60" : ""
                      }`}
                      data-testid={`settings-row-${type}-${it.key}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`font-medium ${hidden ? "line-through text-[#2B3F4D]" : "text-[#0C1824]"}`}>
                          {it.label_no}
                        </div>
                        <div className="text-xs text-[#2B3F4D] mt-0.5">
                          {custom && <span className="inline-block px-1.5 py-0.5 bg-[#BD984D]/20 text-[#A57F39] rounded text-[10px] mr-2">EGEN</span>}
                          {it.photo_on_fail && <span className="inline-flex items-center gap-1 text-[10px]"><Camera className="w-3 h-3" /> Bilde ved avvik</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {hidden && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => restore(type, it.key)}
                            data-testid={`settings-restore-${it.key}`}
                            className="border-[#1DA3B3] text-[#1DA3B3]"
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Gjenopprett
                          </Button>
                        )}
                        {!hidden && custom && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => remove(type, it.key)}
                            data-testid={`settings-remove-${it.key}`}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Slett
                          </Button>
                        )}
                        {!hidden && !custom && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => hide(type, it.key)}
                            data-testid={`settings-hide-${it.key}`}
                            className="border-slate-300 text-slate-600"
                          >
                            Skjul
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
        </TabsContent>

        <TabsContent value="checklist-types" className="mt-6">
          <ChecklistTypesManager />
        </TabsContent>

        <TabsContent value="boats" className="mt-6">
          <BoatsManager />
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <ProductsManager />
        </TabsContent>

        <TabsContent value="rentware" className="mt-6 space-y-5">
          <Card className="p-5 space-y-4" data-testid="rentware-global-card">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#A57F39] font-bold">
                Global token
              </div>
              <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
                Lim inn <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">RTR_ACCESS_TOKEN</span>{" "}
                fra Rentware-dashbordet (Settings → Widgets). Denne deles på tvers av alle widget-snippets
                under, og lastes inn én gang per sesjon.
              </p>
            </div>
            <div className="grid sm:grid-cols-[1fr_180px] gap-3">
              <div>
                <Label>Access token</Label>
                <Input
                  value={rwToken}
                  onChange={(e) => setRwToken(e.target.value)}
                  placeholder="W6043…"
                  data-testid="rentware-token-input"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label>Språk</Label>
                <Select value={rwLocale} onValueChange={setRwLocale}>
                  <SelectTrigger data-testid="rentware-locale-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nb-NO">Norsk (nb-NO)</SelectItem>
                    <SelectItem value="en-US">Engelsk (en-US)</SelectItem>
                    <SelectItem value="de-DE">Tysk (de-DE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <Button onClick={saveRentware} data-testid="rentware-save-global"
                      className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
                Lagre token
              </Button>

              <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="rentware-email-open"
                          className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10">
                    <Mail className="w-4 h-4 mr-1.5" /> Be Rentware om API-tilgang
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>E-postmal til Rentware-support</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-[#2B3F4D]">
                    Kopier teksten under og send til{" "}
                    <a href="mailto:support@rentware.com" className="text-[#1DA3B3] underline">
                      support@rentware.com
                    </a>. Be om REST API eller iCal-feed for full toveis-synkronisering.
                  </p>
                  <Textarea
                    readOnly value={supportEmail} rows={18}
                    className="font-mono text-xs"
                    data-testid="rentware-email-textarea"
                  />
                  <DialogFooter>
                    <Button onClick={copyEmail} data-testid="rentware-email-copy"
                            className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
                      {emailCopied ? <><Check className="w-4 h-4 mr-1.5" /> Kopiert</> :
                                     <><Copy className="w-4 h-4 mr-1.5" /> Kopier e-postmal</>}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </Card>

          <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.24em] text-[#A57F39] font-bold">
              Widget per båt
            </div>
            <p className="text-sm text-[#2B3F4D] max-w-2xl">
              For hver båt går du inn i Rentware → <strong>Settings → Widgets</strong>, velger
              widget-type (f.eks. <em>Booking calendar</em>), velger riktig artikkel (båt), og
              kopierer det genererte HTML-snippet&apos;et inn under. Snippet&apos;et inkluderer typisk en
              custom-element-tag som <span className="font-mono">{"<rtr-...>"}</span>.
            </p>

            {rwLoaded && boats.map((bo) => {
              const d = boatDraft[bo.id] || { embed: "", articleId: "" };
              const update = (patch) =>
                setBoatDraft((p) => ({ ...p, [bo.id]: { ...d, ...patch } }));
              return (
                <Card key={bo.id} className="p-4 space-y-3"
                      data-testid={`rentware-boat-card-${bo.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-semibold text-[#0C1824]">{bo.name}</div>
                    <Button size="sm" onClick={() => saveBoat(bo.id)}
                            data-testid={`rentware-boat-save-${bo.id}`}
                            className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                      Lagre {bo.name.split(" ").slice(-1)[0]}
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                    <div>
                      <Label>Artikkel-ID (valgfri)</Label>
                      <Input
                        value={d.articleId}
                        onChange={(e) => update({ articleId: e.target.value })}
                        placeholder="art_12345"
                        data-testid={`rentware-article-${bo.id}`}
                      />
                    </div>
                    <div>
                      <Label>Widget HTML-snippet</Label>
                      <Textarea
                        rows={4}
                        value={d.embed}
                        onChange={(e) => update({ embed: e.target.value })}
                        placeholder={'<rtr-article article="art_12345"></rtr-article>'}
                        data-testid={`rentware-embed-${bo.id}`}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-5">
          <Card className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <div>
                <h2 className="text-lg font-semibold text-[#0C1824]">Standarddokumenter til kunder</h2>
                <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
                  Disse dokumentene vises automatisk i kundeportalen for <strong>alle</strong> kunder –
                  både eksisterende og nye. Velg PDF-filer som «Velkommen ombord», husregler eller
                  ansvarsfraskrivelser her.
                </p>
              </div>
              <DocumentUploader scope="global" onUploaded={loadGlobalDocs}
                                openTestId="global-doc-upload-open" />
            </div>
            <div className="space-y-2 mt-5" data-testid="global-docs-list">
              {globalDocs.length === 0 && (
                <div className="text-sm text-[#2B3F4D] py-8 text-center border border-dashed border-slate-200 rounded-lg">
                  Ingen standarddokumenter er lastet opp ennå.
                </div>
              )}
              {globalDocs.map((d) => (
                <DocumentRow key={d.id} doc={d} canDelete onDelete={deleteGlobalDoc} />
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="tv-screen" className="mt-6 space-y-5">
          <TvScreenSettings />
        </TabsContent>

        <TabsContent value="drive" className="mt-6 space-y-5">
          <DriveSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DriveSettings() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/drive/health");
        if (alive) setHealth(r.data);
      } catch (e) {
        if (alive) setHealth({ enabled: false, reason: e.message });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const fmtBytes = (n) => {
    if (!n) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0; let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
  };

  if (loading) {
    return <Card className="p-6">Laster Drive-status…</Card>;
  }

  const ok = health?.enabled;

  return (
    <Card className="p-6 space-y-4" data-testid="drive-settings-card">
      <div className="flex items-start gap-3">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center ${
            ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
          }`}
        >
          <HardDrive className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-[#0C1824]">
            Google Drive-integrasjon
          </h2>
          <p className="text-sm text-[#2B3F4D] mt-0.5">
            Alle kundebilder, innsjekk-/utsjekk-bilder og annen media speiles
            automatisk til Shared Drive-en så teamet har full backup og enkel
            tilgang.
          </p>
          <span
            data-testid="drive-status"
            className={`inline-flex items-center gap-1 mt-2 text-[11px] uppercase tracking-widest font-bold rounded px-2 py-0.5 ${
              ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
            }`}
          >
            {ok ? "Tilkoblet" : "Frakoblet"}
          </span>
        </div>
      </div>

      {ok ? (
        <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
          <Detail label="Service Account" value={health.service_account} />
          <Detail
            label="Shared Drive"
            value={health.root_folder_name}
            action={health.root_folder_url && (
              <a
                href={health.root_folder_url}
                target="_blank"
                rel="noreferrer"
                data-testid="drive-open-folder"
                className="text-[#1DA3B3] hover:underline text-xs inline-flex items-center gap-1"
              >
                Åpne <ExternalLink className="w-3 h-3" />
              </a>
            )}
          />
          <Detail label="Rot-id" value={health.root_folder_id} mono />
          {health.limit_bytes ? (
            <Detail
              label="Bruk"
              value={`${fmtBytes(health.usage_bytes)} / ${fmtBytes(health.limit_bytes)}`}
            />
          ) : (
            <Detail
              label="Bruk"
              value={`${fmtBytes(health.usage_bytes)} (Shared Drive kvota deles i workspace)`}
            />
          )}
        </div>
      ) : (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-900">
          <div className="font-semibold">Ikke tilkoblet</div>
          <div className="mt-1 text-xs">{health?.reason || "Ukjent feil"}</div>
        </div>
      )}

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-[#2B3F4D] space-y-1.5">
        <div className="font-semibold text-[#0C1824] uppercase tracking-widest text-[10px]">
          Mappestruktur
        </div>
        <ul className="mt-1 space-y-0.5 font-mono">
          <li>📁 Kundebilder / Til gjennomgang</li>
          <li>📁 Kundebilder / Publiserte  <span className="text-emerald-700 font-sans not-italic">← vises på Kontor-TV</span></li>
          <li>📁 Kundebilder / Avvist</li>
          <li>📁 Innsjekk-Utsjekk / [Båt] / [Booking] / checkin|checkout</li>
          <li>📁 Avvik og skader</li>
          <li>📁 Sjekklister</li>
        </ul>
      </div>
    </Card>
  );
}

function Detail({ label, value, action, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
        {label}
      </div>
      <div className={`text-sm text-[#0C1824] mt-0.5 flex items-center gap-2 ${mono ? "font-mono text-xs" : ""}`}>
        <span className="truncate">{value || "—"}</span>
        {action}
      </div>
    </div>
  );
}
